import type { AriaAttributes } from "react";

import Button, { type IButton } from "@/design-system/button";
import type { IImageLoader } from "@Components/ImageLoader";
import ImageLoader from "@Components/ImageLoader";
import * as stylex from "@stylexjs/stylex";

import { colors, genericStyles } from "../../styles/tokens.stylex";

export interface IRoundLink extends AriaAttributes {
    image: IImageLoader;
    specialCashback?: boolean;
    href?: string;
    onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

export const RoundLink = ({ image, href, specialCashback, onClick, ...rest }: IRoundLink) => {
    const isHref = !!href;
    const isLink = isHref || onClick;

    const tile = <ImageLoader {...stylex.props(styles.roundLogo)} {...image} />;

    if (isLink) {
        const buttonProps: IButton = {
            ...rest,
            as: isHref ? "a" : "button",
            size: "None",
            variant: "none",
            href: isHref ? href : undefined,
            styleX: [styles.roundLink, specialCashback ? styles.isSpecialCB : styles.notSpecialCashback],
            onClick: onClick,
            tabIndex: -1,
        } as unknown as IButton;

        return <Button {...buttonProps}>{tile}</Button>;
    }

    return (
        <div
            {...stylex.props(
                styles.roundLink,
                specialCashback ? styles.isSpecialCB : styles.notSpecialCashback,
            )}
        >
            {tile}
        </div>
    );
};

const styles = stylex.create({
    roundLink: {
        padding: 0,
        borderRadius: genericStyles.borderRadiusFullRound,
        borderStyle: "solid",
        borderWidth: "2px",
        aspectRatio: "1/1",
        backgroundColor: colors.b_Emphasis,
        display: "block",
        position: "relative",
        transitionDuration: ".3s",
        transitionProperty: "border-color",
        transitionTimingFunction: "ease-out",
        width: "100%",

        "::after": {
            backgroundImage: `radial-gradient(
                ellipse at center,
                rgba(128, 128, 128, 0.8) 0.07%,
                rgba(186, 186, 186, 0.45) 44.98%,
                rgba(226, 226, 226, 0.18) 63.79%,
                rgba(255, 255, 255, 0) 89.18%
            )`,

            content: "",
            position: "absolute",
            zIndex: -2,
            bottom: "-8px",
            height: "15px",
            left: 0,
            marginBottom: 0,
            marginLeft: "auto",
            marginRight: "auto",
            marginTop: 0,
            right: 0,
            width: "60%",
        },
    },

    isSpecialCB: {
        borderColor: {
            default: colors.b_BrandMagenta,
            ":hover": colors.b_HoverBrandMagenta,
        },
    },

    notSpecialCashback: {
        borderColor: {
            default: colors.fg_Tertiary,
            ":hover": colors.b_HoverBrandMagenta,
        },
    },

    roundLogo: {
        padding: "15%",
        objectFit: "contain",
        position: "relative",
        height: "100%",
        width: "100%",
    },
});
